import { Box, Button, Checkbox, CircularProgress, FormControlLabel, Paper, Typography } from '@mui/material';
import { JSX, useEffect, useMemo } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { currentTenantId } from '../../../library/brandingCache';
import { fieldProps, useValidatedForm } from '../../../library/formValidation';
import { splitApiError } from '../../../library/useApiFormError';
import { IReminderSettings, REMINDER_CHANNELS, ReminderSettingsDraft, toReminderSettingsDraft, toReminderSettingsRequest } from '../../../library/models/reminderSettings';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactMutation, useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import { useSnackbar } from '../../layout/snackbarProvider';
import { AppTimePicker } from '../../pickers/AppTimePicker';
import { createReminderSettingsSchema } from './reminderSettingsSchema';
import { actionsStyles, blockedSaveStyles, cardHintStyles, cardStyles, cardTitleStyles, channelHintStyles, channelListStyles, sectionStyles, sendTimeFieldStyles } from './reminderSettings.styles';

/** The label and caption keys for each channel, by name. */
const CHANNEL_LABELS: Record<string, string> = {
    EMAIL: ResourcesIds.REMINDER_SETTINGS_CHANNEL_EMAIL,
    SMS: ResourcesIds.REMINDER_SETTINGS_CHANNEL_SMS,
    WHATSAPP: ResourcesIds.REMINDER_SETTINGS_CHANNEL_WHATSAPP,
};

/**
 * Where a clinic chooses when its patients hear about tomorrow.
 *
 * Three settings and no more: whether to send, at what time, and over what. Everything else
 * about a reminder is the same for every clinic on purpose -- one email per patient covering
 * their whole day, sent the day before.
 *
 * Every channel is drawn, and the ones the platform cannot yet send over are **disabled
 * rather than hidden**. An admin should be able to see that SMS is coming without being able
 * to switch on a setting nothing would act on; the API refuses one either way, and
 * `availableChannels` is what decides, so adding a sender on the backend lights the row up
 * with no release here.
 *
 * react-hook-form and Yup like every other form in the app. The two rules the API enforces
 * live in `reminderSettingsSchema`, stated per field so each one lands on the control at
 * fault -- this panel used to draw no field-level errors at all, so a half-typed time gave
 * an admin a dead Save button and nothing else.
 */
export const ReminderSettings = (): JSX.Element => {
    const { t: translate } = useTranslation();
    const { showSnackbar } = useSnackbar();

    // Keyed by tenant, or somebody in two clinics is served the first one's settings.
    const { data, isLoading, refetch } = useReactQuery<IReminderSettings>(
        services.reminderSettings.getSettings(currentTenantId())
    );

    const saveSettings = useReactMutation(services.reminderSettings.saveSettingsMutation());

    const schema = useMemo(() => createReminderSettingsSchema(translate), [translate]);

    const {
        control, handleSubmit, reset, setValue, setError,
        formState: { errors, isValid },
    } = useValidatedForm<ReminderSettingsDraft>(schema, {
        defaultValues: toReminderSettingsDraft(undefined),
    });

    /** Reseeded from the server, so the fields open on what is actually happening. */
    useEffect(() => {
        if (data) {
            reset(toReminderSettingsDraft(data));
        }
    }, [data, reset]);

    const current: ReminderSettingsDraft = useWatch({ control }) as ReminderSettingsDraft;
    const available: string[] = data?.availableChannels ?? ['EMAIL'];

    const knownFields: string[] = useMemo(() => Object.keys(schema.fields), [schema]);

    const onValid = (values: ReminderSettingsDraft): void => {
        saveSettings.mutate(toReminderSettingsRequest(values), {
            onSuccess: () => {
                showSnackbar(translate(ResourcesIds.REMINDER_SETTINGS_SAVE_SUCCESS), 'success');
                refetch();
            },
            // Split rather than snackbarred whole: a per-field rejection used to be
            // flattened into one generic line here and the field never marked.
            onError: (error: Error) => {
                const outcome = splitApiError(error, knownFields, translate,
                    ResourcesIds.REMINDER_SETTINGS_SAVE_ERROR);

                Object.entries(outcome.fieldErrors).forEach(([field, message]) =>
                    setError(field as keyof ReminderSettingsDraft, { type: 'server', message }));

                if (outcome.formError) {
                    showSnackbar(outcome.formError, 'error');
                }
            },
        });
    };

    if (isLoading) {
        return (
            <Box sx={sectionStyles}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={sectionStyles}>
            <Paper sx={cardStyles} elevation={0}>
                <Typography variant="h6" sx={cardTitleStyles}>
                    {translate(ResourcesIds.REMINDER_SETTINGS_TITLE)}
                </Typography>
                <Typography variant="body2" sx={cardHintStyles}>
                    {translate(ResourcesIds.REMINDER_SETTINGS_DESCRIPTION)}
                </Typography>

                <FormControlLabel
                    control={
                        <Controller
                            name="enabled"
                            control={control}
                            render={({ field }) => (
                                <Checkbox
                                    checked={!!field.value}
                                    onChange={(_event, checked: boolean) =>
                                        setValue('enabled', checked, { shouldValidate: true })}
                                />
                            )}
                        />
                    }
                    label={translate(ResourcesIds.REMINDER_SETTINGS_ENABLED_LABEL)}
                />
            </Paper>

            <Paper sx={cardStyles} elevation={0}>
                <Typography variant="h6" sx={cardTitleStyles}>
                    {translate(ResourcesIds.REMINDER_SETTINGS_SEND_TIME_LABEL)}
                </Typography>
                <Typography variant="body2" sx={cardHintStyles}>
                    {translate(ResourcesIds.REMINDER_SETTINGS_SEND_TIME_HELPER)}
                </Typography>

                <Controller
                    name="sendTime"
                    control={control}
                    render={({ field }) => (
                        <AppTimePicker
                            label={translate(ResourcesIds.REMINDER_SETTINGS_SEND_TIME_LABEL)}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            name={field.name}
                            inputRef={field.ref}
                            // A reminder goes out on the quarter, never at 09:07.
                            minutesStep={15}
                            fullWidth
                            sx={sendTimeFieldStyles}
                            disabled={!current.enabled}
                            {...fieldProps(errors, 'sendTime')}
                        />
                    )}
                />
            </Paper>

            <Paper sx={cardStyles} elevation={0}>
                <Typography variant="h6" sx={cardTitleStyles}>
                    {translate(ResourcesIds.REMINDER_SETTINGS_CHANNELS_LABEL)}
                </Typography>

                <Box sx={channelListStyles}>
                    {REMINDER_CHANNELS.map((channel: string) => {
                        const unavailable: boolean = !available.includes(channel);

                        return (
                            <Box key={channel}>
                                <FormControlLabel
                                    control={
                                        <Controller
                                            name="channels"
                                            control={control}
                                            render={({ field }) => (
                                                <Checkbox
                                                    checked={(field.value ?? []).includes(channel)}
                                                    disabled={unavailable || !current.enabled}
                                                    onChange={(_event, checked: boolean) =>
                                                        field.onChange(checked
                                                            ? [...(field.value ?? []), channel]
                                                            : (field.value ?? []).filter(
                                                                (name: string) => name !== channel))
                                                    }
                                                />
                                            )}
                                        />
                                    }
                                    label={translate(CHANNEL_LABELS[channel])}
                                />
                                {unavailable && (
                                    <Typography variant="caption" component="p" sx={channelHintStyles}>
                                        {translate(ResourcesIds.REMINDER_SETTINGS_CHANNEL_UNAVAILABLE)}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Paper>

            <Box sx={actionsStyles}>
                {/* Beside a disabled Save, never instead of one: a greyed button with no
                    explanation is the complaint this panel used to attract. */}
                {!isValid && (
                    <Typography variant="body2" sx={blockedSaveStyles}>
                        {translate(ResourcesIds.FORM_BLOCKED_SAVE)}
                    </Typography>
                )}
                <Button
                    variant="contained"
                    onClick={handleSubmit(onValid)}
                    disabled={!isValid || saveSettings.isPending}
                >
                    {translate(ResourcesIds.REMINDER_SETTINGS_SAVE)}
                </Button>
            </Box>
        </Box>
    );
};
